import { useState } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import DynamicField from "components/form-fields/DynamicField/DynamicField"
import RJTableCommentContainer from "../../../components/RJTableCommentContainer/RJTableCommentContainer"
import CellValidation from "../../../components/CellValidation/CellValidation"
import CharCounter from "components/CharCounter/CharCounter"
import TableForkActions from "../../../components/TableForkActions/TableForkActions"

// context
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringCommentsStore"
import useAuthoringDataStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringDataStore"

// helpers
import { translateProps } from "../../helpers/helpers"

const registries = ["ctg", "eudract"]

const AdverseProperties = ({ schema, adverseData, setAdverseData }) => {
  const { tableView } = useResultsTableViewStore()

  const { forkedStudyData } = useAuthoringDataStore(
    useShallow((state) => ({
      forkedStudyData: state.forkedStudyData,
    }))
  )

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
    }))
  )

  const onChange = (e, key, hasFork, forkedRegistry) => {
    let tempData = { ...adverseData }

    Object.keys(tempData).forEach((registry) => {
      let fieldSchema = schema.properties[registry].properties
      if (
        (formView?.id === "global" && registry === forkedRegistry && hasFork) ||
        (formView?.id !== "global" && hasFork && registry === tableView) ||
        !hasFork
      ) {
        if (key === "assessment_type") {
          if (registry !== tableView) tempData[registry][key] = translateProps[tableView][registry][key][e]
          else tempData[registry][key] = e
        } else if (key === "source_vocabulary" && (e === "MedDRA" || e === "SNOMED CT")) {
          tempData[registry][key] = e
          tempData["eudract"]["dictionary_name"] = e
          tempData["eudract"]["other_dictionary_name"] = null
        } else if (key === "source_vocabulary") {
          tempData["ctg"][key] = e
          tempData["eudract"]["other_dictionary_name"] = e
          tempData["eudract"]["dictionary_overridden"] = "Yes"
          tempData["eudract"]["dictionary_name"] = "Other (specify)"
        } else if (key === "other_dictionary_name") {
          if (tableView === "eudract") {
            tempData[registry][key] = e
            tempData["ctg"]["source_vocabulary"] = e
          }
        } else if (fieldSchema[key]) {
          tempData[registry][key] = e
        }

        if (tableView === "eudract" && adverseData[tableView]["dictionary_name"] !== "Other (specify)") {
          tempData[tableView]["other_dictionary_name"] = null
          tempData["ctg"]["source_vocabulary"] = tempData["eudract"]["dictionary_name"]
        }
      }
    })

    setAdverseData(tempData)
  }

  const deleteForkCallback = ({ registry, key }) => {
    let tempData = { ...adverseData }

    registries.forEach((reg) => {
      if (reg !== registry) {
        tempData[registry][key] = tempData[reg][key]
      }
    })
    setAdverseData(tempData)
  }

  return Object.keys(schema.properties[tableView].properties).map((key) => {
    let fieldSchema = schema.properties[tableView].properties
    let disabled = false

    if (
      tableView === "eudract" &&
      adverseData?.[tableView]?.["dictionary_name"] !== "Other (specify)" &&
      key === "other_dictionary_name"
    ) {
      disabled = true
    }

    const errorKey = `results_adverse_events_adverse_events_table_${tableView}_${key}`
    const jpath = `ReportedEvents.ae-prop-${key}`
    const hasFork = forkedStudyData?.["ctg"]?.[jpath] || forkedStudyData?.["eudract"]?.[jpath]

    const forkFieldSchema = schema.properties["eudract"].properties

    let showCharCount = false

    if (!readOnly) {
      if (fieldSchema[key]?.charCountEnabled) {
        showCharCount = true

        if (formView?.id === "global") {
          showCharCount = fieldSchema[key].globalMaxChars ? true : false
        }
      }
    }

    return (
      <tr key={`${tableView}-ae-prop-${key}`}>
        <PropertyHeader
          title={fieldSchema[key].title}
          fieldKey={key}
          jpath={jpath}
          deleteForkCallback={(val) => deleteForkCallback({ ...val, key })}
          tableView={tableView}
        />
        <td style={{ width: "600px" }}>
          {tableView !== "eudract" && (
            <div>
              {hasFork && formView?.id === "global" && (
                <TableForkActions
                  registry="ctg"
                  isFork={forkedStudyData?.["ctg"]?.[jpath]}
                  jpath={jpath}
                  style={{ marginBottom: 4 }}
                  deleteForkCallback={(val) => deleteForkCallback({ ...val, key })}
                />
              )}
              <DynamicField
                type={fieldSchema[key].type}
                fieldProps={{
                  readOnly,
                  disabled,
                  value: adverseData?.[tableView]?.[key],
                  options: fieldSchema[key].enum,
                  onChange: (e) => onChange(e, key, hasFork, "ctg"),
                }}
              />
              {showCharCount && (
                <div className="char-counter-container">
                  <CharCounter
                    value={adverseData?.[tableView]?.[key]}
                    maxChars={
                      formView?.id == "ctg" || hasFork ? fieldSchema[key]?.maxChars : fieldSchema[key]?.globalMaxChars
                    }
                    errorMessage={fieldSchema[key]?.globalMaxCharsError}
                  />
                </div>
              )}
            </div>
          )}
          {((formView?.id === "global" && hasFork) || tableView === "eudract") && (
            <div style={{ marginTop: 12 }}>
              {hasFork && formView?.id === "global" && (
                <TableForkActions
                  registry="eudract"
                  isFork={forkedStudyData?.["eudract"]?.[jpath]}
                  jpath={jpath}
                  style={{ marginBottom: 4 }}
                  deleteForkCallback={(val) => deleteForkCallback({ ...val, key })}
                />
              )}
              <DynamicField
                type={fieldSchema[key].type}
                fieldProps={{
                  readOnly,
                  disabled,
                  value: adverseData?.["eudract"]?.[key],
                  options: fieldSchema[key].enum,
                  onChange: (e) => onChange(e, key, hasFork, "eudract"),
                }}
              />
              {showCharCount && (
                <div className="char-counter-container">
                  <CharCounter
                    value={adverseData?.["eudract"]?.[key]}
                    maxChars={forkFieldSchema[key]?.maxChars}
                  />
                </div>
              )}
            </div>
          )}

          <CellValidation
            table="adverse"
            errorKey={errorKey}
          />
        </td>
      </tr>
    )
  })
}

export default AdverseProperties

const PropertyHeader = ({ title, fieldKey, jpath, deleteForkCallback, tableView }) => {
  const [openCommentsForm, setOpenCommentsForm] = useState(false)
  const [showActions, setShowActions] = useState(false)

  const { commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      commentsMap: state.commentsMap,
    }))
  )

  let enableForking = false
  if (fieldKey === "notes") enableForking = true

  return (
    <td
      className="header-column"
      colSpan={2}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      style={{ position: "relative" }}
    >
      {title}
      <div className="row-actions-container">
        <RJTableCommentContainer
          label={title}
          jpath={jpath}
          registry={tableView}
          openCommentsForm={openCommentsForm}
          setOpenCommentsForm={setOpenCommentsForm}
          setShowMenu={setShowActions}
          showMenu={showActions || commentsMap[jpath] || openCommentsForm}
          modalStyle={{ top: 24 }}
          enableForking={enableForking}
          deleteForkCallback={deleteForkCallback}
        />
      </div>
    </td>
  )
}
